import React, { useState } from "react";
import { Progress } from "antd";
import { DownOutlined, RightOutlined, UpOutlined } from "@ant-design/icons";
import { SbAvatar, SbTag, SbTooltip } from "uiCore";
import { getScoreHex } from "Utils/scoreColor.utils";
import { getSuccessionScoreColor } from "Utils/successionScore.utils";
import { useMatchThresholds, useSuccessionThresholds } from "Store/useTalentMobilitySettingsStore";
import { STATIC_SCORE_PLACEHOLDER, hasPlaceholderScores } from "../successionScorePlaceholder.constants";
import {
  EmployeeCardStyled,
  CardHeaderRowStyled,
  CardIdentityStyled,
  CardRoleTitleStyled,
  CardEmployeeNameStyled,
  CardNameRowStyled,
  EmployeeTenureStyled,
  InlineEmployeePickerStyled,
  InlineEmployeePickerTitleStyled,
  InlineEmployeeOptionStyled,
  ToggleButtonStyled,
  ContextBadgeStyled,
  CardMetaGridStyled,
  CardMetaRowStyled,
  CardMetaLabelStyled,
  CardMetaValueStyled,
  GaugeStyled,
  PlaceholderBadgeStyled,
  SuccessionRowStyled,
  SuccessionBarWrapStyled,
  SuccessionValueStyled,
  SuccessorsToggleStyled,
  SuccessorsPanelStyled,
} from "../SuccessionPlanningV2.styles";

const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

// Renders one organizational position. `positionNode.employees` holds every
// occupant of this position (see successionPositionGrouping.utils) —
// `selectedEmployee` is whichever one is currently chosen, and its own
// `children` are what render below this card. When a position has more than
// one occupant, clicking anywhere on the card expands a connected panel just
// underneath it — listing the other occupants, in-flow — no floating
// dropdown overlay.
//
// Score fields (readiness_score, tenure_months, matches_above_threshold,
// succession_score) read as `selectedEmployee.<field> ??
// STATIC_SCORE_PLACEHOLDER.<field>` — nullish coalescing, so a real `0`/
// `false` from the API is never overwritten, only an actually-missing field
// falls back to the shared static placeholder (see
// successionScorePlaceholder.constants.js). Whenever any of those fields is
// still placeholder-sourced, a small "Placeholder data" badge says so, so
// these static demo numbers are never mistaken for a real HR assessment.
// "Top potential successors" always shows a static empty state — never a
// real or fake employee name, since no successor-matching data exists yet.
const SuccessionEmployeeCard = ({ positionNode, selectedEmployee, hasChildren, collapsed, onToggleCollapse, onSelectEmployee }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [successorsOpen, setSuccessorsOpen] = useState(false);
  const matchThresholds = useMatchThresholds();
  const successionThresholds = useSuccessionThresholds();

  const {
    employee_name: employeeName,
    current_role: currentRole,
    legal_entity: legalEntity,
    is_active: isActive,
  } = selectedEmployee;

  const { contextOnly, employees, role } = positionNode;
  const roleTitle = role || currentRole;
  const hasMultipleEmployees = employees.length > 1;

  const readinessScore = selectedEmployee.readiness_score ?? STATIC_SCORE_PLACEHOLDER.readiness_score;
  const tenureMonths = selectedEmployee.tenure_months ?? STATIC_SCORE_PLACEHOLDER.tenure_months;
  const matchesAboveThreshold = selectedEmployee.matches_above_threshold ?? STATIC_SCORE_PLACEHOLDER.matches_above_threshold;
  const successionScoreValue = selectedEmployee.succession_score ?? STATIC_SCORE_PLACEHOLDER.succession_score;
  const readyForRole = selectedEmployee.ready_for_role ?? (readinessScore != null && readinessScore >= successionThresholds.readyNow);
  const showsPlaceholderScores = hasPlaceholderScores(selectedEmployee);

  const toggleLabel = `${collapsed ? "Expand" : "Collapse"} ${roleTitle}'s direct reports`;
  const pickerLabel = `${roleTitle} — ${employees.length} employees hold this position, ${pickerOpen ? "hide" : "show"} the list`;
  const successorsToggleLabel = `${successorsOpen ? "Hide" : "Show"} top potential successors for ${roleTitle}`;

  const handleSuccessorsToggle = (e) => {
    e.stopPropagation();
    setSuccessorsOpen((open) => !open);
  };

  // Clicking anywhere on the card (not just the employee name) reveals the
  // other occupants of a shared position — only when there's more than one
  // to pick from. Ignores clicks/keypresses that started on a nested real
  // button (e.g. the collapse chevron) so those don't also toggle the picker.
  const handleCardClick = (e) => {
    if (!hasMultipleEmployees || e.target.closest("button")) return;
    setPickerOpen((open) => !open);
  };

  const handleCardKeyDown = (e) => {
    if (!hasMultipleEmployees || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPickerOpen((open) => !open);
    }
  };

  const handlePickEmployee = (e, employeeId) => {
    e.stopPropagation();
    onSelectEmployee(employeeId);
    setPickerOpen(false);
  };

  const handleCollapseToggle = (e) => {
    e.stopPropagation();
    onToggleCollapse();
  };

  // Name and tenure are deliberately separate elements (not one interpolated
  // string) so the employee's name stays an exact, independently-queryable
  // piece of text — e.g. for navigation, tests, or anything else that looks
  // the employee up by name alone.
  const nameWithTenure = (
    <>
      <span>{employeeName}</span> <EmployeeTenureStyled>· {tenureMonths} mo</EmployeeTenureStyled>
    </>
  );

  const employeeNameBlock = hasMultipleEmployees ? (
    <CardNameRowStyled>
      <CardEmployeeNameStyled title={`${employeeName} · ${tenureMonths} mo`}>{nameWithTenure}</CardEmployeeNameStyled>
      {pickerOpen ? <UpOutlined style={{ fontSize: 9 }} /> : <DownOutlined style={{ fontSize: 9 }} />}
    </CardNameRowStyled>
  ) : (
    <CardEmployeeNameStyled title={`${employeeName} · ${tenureMonths} mo`}>{nameWithTenure}</CardEmployeeNameStyled>
  );

  if (contextOnly) {
    return (
      <EmployeeCardStyled $contextOnly>
        <CardHeaderRowStyled>
          <CardIdentityStyled>
            <CardRoleTitleStyled title={roleTitle}>{roleTitle}</CardRoleTitleStyled>
            <CardEmployeeNameStyled title={employeeName}>{employeeName}</CardEmployeeNameStyled>
          </CardIdentityStyled>
          {hasChildren && (
            <ToggleButtonStyled type="button" aria-label={toggleLabel} aria-expanded={!collapsed} onClick={handleCollapseToggle}>
              {collapsed ? <RightOutlined /> : <DownOutlined />}
            </ToggleButtonStyled>
          )}
        </CardHeaderRowStyled>
        <ContextBadgeStyled>Hierarchy context</ContextBadgeStyled>
      </EmployeeCardStyled>
    );
  }

  return (
    <>
      <EmployeeCardStyled
        role={hasMultipleEmployees ? "button" : undefined}
        tabIndex={hasMultipleEmployees ? 0 : undefined}
        aria-expanded={hasMultipleEmployees ? pickerOpen : undefined}
        aria-label={hasMultipleEmployees ? pickerLabel : undefined}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <CardHeaderRowStyled>
          <SbAvatar>{getInitials(employeeName)}</SbAvatar>
          <CardIdentityStyled>
            <CardRoleTitleStyled title={roleTitle}>{roleTitle}</CardRoleTitleStyled>
            {employeeNameBlock}
          </CardIdentityStyled>
          {readinessScore != null && (
            <GaugeStyled>
              <Progress
                type="circle"
                percent={readinessScore}
                size={28}
                strokeWidth={10}
                strokeColor={getScoreHex(readinessScore, matchThresholds)}
                format={(pct) => pct}
              />
            </GaugeStyled>
          )}
          {hasChildren && (
            <ToggleButtonStyled type="button" aria-label={toggleLabel} aria-expanded={!collapsed} onClick={handleCollapseToggle}>
              {collapsed ? <RightOutlined /> : <DownOutlined />}
            </ToggleButtonStyled>
          )}
        </CardHeaderRowStyled>

        {showsPlaceholderScores && (
          <SbTooltip content="Static demo values — not a real HR assessment. Will switch to live data automatically once the backend provides it.">
            <PlaceholderBadgeStyled>Placeholder data</PlaceholderBadgeStyled>
          </SbTooltip>
        )}

        <CardMetaGridStyled>
          <CardMetaRowStyled>
            <CardMetaLabelStyled>Ready for Role</CardMetaLabelStyled>
            <SbTag color={readyForRole ? "success" : "default"} style={{ margin: 0 }}>{readyForRole ? "Yes" : "No"}</SbTag>
          </CardMetaRowStyled>
          <CardMetaRowStyled>
            <CardMetaLabelStyled>Matches (&gt;{matchThresholds.green}%)</CardMetaLabelStyled>
            <SbTag color={matchesAboveThreshold > 0 ? "success" : "default"} style={{ margin: 0 }}>{matchesAboveThreshold}</SbTag>
          </CardMetaRowStyled>
          <SuccessionRowStyled>
            <CardMetaLabelStyled>Succession Score</CardMetaLabelStyled>
            {successionScoreValue != null ? (
              <>
                <SuccessionBarWrapStyled>
                  <Progress
                    percent={successionScoreValue}
                    showInfo={false}
                    size="small"
                    strokeColor={getScoreHex(successionScoreValue, { green: 80, red: 60 })}
                  />
                </SuccessionBarWrapStyled>
                <SuccessionValueStyled style={{ color: getScoreHex(successionScoreValue, { green: 80, red: 60 }) }}>
                  {successionScoreValue}
                </SuccessionValueStyled>
              </>
            ) : (
              <SbTag color={getSuccessionScoreColor(null)} style={{ margin: 0 }}>No Data</SbTag>
            )}
          </SuccessionRowStyled>
        </CardMetaGridStyled>

        {/* Business Unit / Function / Sub-Function / Level intentionally hidden for now — see task history. */}

        <CardHeaderRowStyled as="div" style={{ justifyContent: "space-between", marginTop: 4 }}>
          {legalEntity ? (
            <SbTooltip content={legalEntity}>
              <CardMetaValueStyled style={{ fontSize: 10.5, opacity: 0.75 }}>{legalEntity}</CardMetaValueStyled>
            </SbTooltip>
          ) : (
            <span />
          )}
          {isActive === false && <SbTag color="error" style={{ margin: 0 }}>Inactive</SbTag>}
        </CardHeaderRowStyled>

        <SuccessorsToggleStyled
          type="button"
          aria-label={successorsToggleLabel}
          aria-expanded={successorsOpen}
          onClick={handleSuccessorsToggle}
        >
          Top potential successors {successorsOpen ? <UpOutlined style={{ fontSize: 9 }} /> : <DownOutlined style={{ fontSize: 9 }} />}
        </SuccessorsToggleStyled>
      </EmployeeCardStyled>

      {successorsOpen && (
        <SuccessorsPanelStyled>No matched successors yet.</SuccessorsPanelStyled>
      )}

      {hasMultipleEmployees && pickerOpen && (
        <InlineEmployeePickerStyled role="listbox" aria-label={`Employees in the ${roleTitle} position`}>
          <InlineEmployeePickerTitleStyled>Other employees in this position</InlineEmployeePickerTitleStyled>
          {employees.map((emp) => (
            <InlineEmployeeOptionStyled
              key={emp.employee_id}
              type="button"
              role="option"
              aria-selected={emp.employee_id === selectedEmployee.employee_id}
              $selected={emp.employee_id === selectedEmployee.employee_id}
              onClick={(e) => handlePickEmployee(e, emp.employee_id)}
            >
              {emp.employee_name}
            </InlineEmployeeOptionStyled>
          ))}
        </InlineEmployeePickerStyled>
      )}
    </>
  );
};

export default SuccessionEmployeeCard;
